'use client';

import { useState } from 'react';

import Link from 'next/link';

import { Crown, Heart, ShieldCheck, Sparkles, UserRound } from 'lucide-react';

/**
 * "CREATE YOUR OWN" tile injected into the character grids (home + catalog).
 * Design 1:1 from the mock: the purple-trio artwork as the card, Custom
 * Character badge on top, title/subtitle, three feature icons and the
 * CREATE NOW button in the artwork's dark lower half. Links to /create.
 */
export function CreateCharacterPromoCard() {
  const [hovered, setHovered] = useState(false);

  const pop = (scale: number) => ({
    transition: 'transform 0.4s ease',
    transform: hovered ? `scale(${scale})` : 'scale(1)',
  });

  return (
    <Link
      href="/create"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Create your own AI character"
      className="relative block w-full overflow-hidden rounded-3xl text-center"
      style={{
        containerType: 'inline-size',
        aspectRatio: '324 / 520',
        background:
          "url('/images/backgrounds/create-gf-popup.avif') center / cover no-repeat, radial-gradient(115% 90% at 50% 22%, #6d28d9 0%, #4c1d95 52%, #2a0f54 100%)",
        border: '1px solid rgba(167,139,250,0.45)',
        transition:
          'transform 0.4s ease, filter 0.4s ease, box-shadow 0.4s ease',
        transform: hovered ? 'scale(1.02)' : 'scale(1)',
        filter: hovered ? 'brightness(1.08)' : 'brightness(1)',
        boxShadow: hovered
          ? '0 0 34px rgba(168,85,247,0.6), 0 0 85px rgba(109,40,217,0.5), inset 0 0 30px rgba(168,85,247,0.16)'
          : '0 0 10px rgba(168,85,247,0.2), inset 0 0 22px rgba(168,85,247,0.10)',
      }}
    >
      {/* Bottom scrim so text always reads over the art */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0"
        style={{
          height: '64%',
          background:
            'linear-gradient(to top, rgba(10,4,20,0.96) 0%, rgba(10,4,20,0.7) 55%, transparent 100%)',
        }}
      />

      {/* Custom Character badge */}
      <span
        className="absolute inset-x-0 top-0 flex justify-center"
        style={{ paddingTop: '5cqw' }}
      >
        <span
          className="inline-flex items-center font-semibold text-purple-100"
          style={{
            gap: '1.6cqw',
            padding: '1.8cqw 4.5cqw',
            fontSize: '4.2cqw',
            borderRadius: '999px',
            border: '1px solid rgba(216,180,254,0.5)',
            background: 'rgba(20,8,40,0.55)',
            ...pop(1.05),
          }}
        >
          <Sparkles style={{ width: '4.2cqw', height: '4.2cqw' }} />
          Custom Character
        </span>
      </span>

      {/* Content in the dark lower half */}
      <span
        className="absolute inset-x-0 bottom-0 flex flex-col items-center"
        style={{ padding: '0 6cqw 5.5cqw', gap: '2.8cqw' }}
      >
        <span
          className="flex flex-col items-center font-extrabold"
          style={{ lineHeight: 1.02, ...pop(1.04) }}
        >
          <span className="text-white" style={{ fontSize: '13cqw' }}>
            CREATE
            <span
              aria-hidden
              style={{ fontSize: '7cqw', verticalAlign: 'top' }}
            >
              ✦
            </span>
          </span>
          <span
            style={{
              fontSize: '13cqw',
              background: 'linear-gradient(90deg,#e9d5ff,#c084fc,#a855f7)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              filter: 'drop-shadow(0 2px 12px rgba(168,85,247,0.45))',
            }}
          >
            YOUR OWN
          </span>
        </span>

        <span
          className="text-gray-300"
          style={{ fontSize: '4.4cqw', lineHeight: 1.45, maxWidth: '80cqw' }}
        >
          Build your fantasy with a custom AI companion designed your way.
        </span>

        {/* Feature icons */}
        <span
          className="flex w-full items-stretch justify-center"
          style={{ gap: '0', ...pop(1.03) }}
        >
          {[
            { Icon: UserRound, label: 'Choose looks' },
            { Icon: Heart, label: 'Set personality' },
            { Icon: Crown, label: 'Make her yours' },
          ].map(({ Icon, label }, i) => (
            <span
              key={label}
              className="flex flex-1 flex-col items-center"
              style={{
                gap: '1.8cqw',
                borderLeft: i > 0 ? '1px solid rgba(216,180,254,0.18)' : 'none',
              }}
            >
              <span
                className="flex items-center justify-center"
                style={{
                  width: '13cqw',
                  height: '13cqw',
                  borderRadius: '3.4cqw',
                  border: '1px solid rgba(216,132,255,0.55)',
                  background: 'rgba(30,12,55,0.5)',
                  boxShadow: '0 0 12px rgba(168,85,247,0.25)',
                }}
              >
                <Icon
                  className="text-pink-300"
                  style={{ width: '6cqw', height: '6cqw' }}
                />
              </span>
              <span className="text-gray-200" style={{ fontSize: '3.4cqw' }}>
                {label}
              </span>
            </span>
          ))}
        </span>

        {/* CTA */}
        <span
          className="relative inline-flex w-full items-center justify-center overflow-hidden font-bold text-white"
          style={{
            padding: '3.6cqw 4cqw',
            fontSize: '5.8cqw',
            borderRadius: '999px',
            background: 'linear-gradient(90deg,#ec4899,#a855f7)',
            boxShadow: '0 0 22px rgba(168,85,247,0.55)',
            ...pop(1.05),
          }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)',
              transition: 'transform 0.7s ease-out',
              transform: hovered ? 'translateX(100%)' : 'translateX(-100%)',
            }}
          />
          <span
            className="relative z-10 inline-flex items-center"
            style={{ gap: '2cqw' }}
          >
            CREATE NOW
            <Sparkles style={{ width: '5.2cqw', height: '5.2cqw' }} />
          </span>
        </span>

        {/* Trust footer */}
        <span
          className="inline-flex items-center font-semibold text-purple-300/80 uppercase"
          style={{ gap: '1.6cqw', fontSize: '3.4cqw', letterSpacing: '0.14em' }}
        >
          <ShieldCheck style={{ width: '3.8cqw', height: '3.8cqw' }} />
          Private · Secure · Yours
        </span>
      </span>
    </Link>
  );
}
